/**
 * Step 2 recipient-row validation — the required-field rules the Recipients table uses to flag empty
 * cells and count incomplete rows. The flag is a NON-BLOCKING warning: the sender can keep editing and
 * come back, so Continue is not gated on it. The server is the source of truth — a campaign with a
 * recipient list whose rows are still incomplete is refused at LAUNCH (RecipientsMissingEmailError /
 * RecipientsIncompleteError), before any charge; this flag is the early, in-table heads-up.
 *
 * The required set mirrors the add/import flows: Recipient First Name, Sender First Name and Sender
 * Company are always required; Recipient Email is required ONLY when GfG sends the gift by email (a
 * manual/universal "link to a list" roster is never emailed, so its email is optional). Everything
 * else — last names, company, country, signature, send date/time — is optional.
 */

import type { Recipient } from "@/interfaces/CampaignBuilder";

/** Required on every recipient regardless of send method. */
const ALWAYS_REQUIRED_RECIPIENT_FIELDS = [
  "firstName",
  "senderFirst",
  "senderCompany",
] as const satisfies readonly (keyof Recipient)[];

/**
 * The recipient columns that must be filled. `requireEmail` is true for a gfg campaign (GfG emails
 * each recipient) and false for a manual/universal "link to a list" roster (handed links, never
 * emailed), matching the OneSchema overrides, the Add-recipient form and the recipient DTO.
 */
export function requiredRecipientFields(requireEmail: boolean): readonly (keyof Recipient)[] {
  return requireEmail
    ? [...ALWAYS_REQUIRED_RECIPIENT_FIELDS, "email"]
    : ALWAYS_REQUIRED_RECIPIENT_FIELDS;
}

/** The required fields this recipient has left blank (whitespace counts as blank). */
export function missingRecipientFields(
  recipient: Recipient,
  requireEmail: boolean,
): (keyof Recipient)[] {
  return requiredRecipientFields(requireEmail).filter((field) => !(recipient[field] ?? "").trim());
}

/** True when a recipient row has every required field filled. */
export function isRecipientComplete(recipient: Recipient, requireEmail: boolean): boolean {
  return missingRecipientFields(recipient, requireEmail).length === 0;
}
